import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { 
  getTripPlansByUser, 
  searchTripPlans, 
  getTripPlansByTheme,
  deleteTripPlan,
  canUserDeleteTripPlan 
} from '../../utils/tripPlanUtils';
import { tripPlanModel } from '../../data/tripPlanModel';
import TripPlanCard from './TripPlanCard';

const TripPlanList = ({ 
  onEditTripPlan, 
  onViewTripPlan, 
  onSelectTripPlan,
  isSelectable = false,
  selectedTripPlan = null 
}) => {
  const { currentUser } = useAuth();
  const [tripPlans, setTripPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTheme, setSelectedTheme] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  // Load trip plans
  const loadTripPlans = async () => {
    if (!currentUser?.uid) return;
    
    try {
      setLoading(true);
      setError(null);
      
      let result;
      if (searchTerm) {
        result = await searchTripPlans(searchTerm, currentUser.uid);
      } else if (selectedTheme) {
        result = await getTripPlansByTheme(selectedTheme, currentUser.uid);
      } else {
        result = await getTripPlansByUser(currentUser.uid);
      }
      
      if (result.success) {
        let filteredPlans = result.data;
        
        // Filter by status if selected
        if (selectedStatus) {
          filteredPlans = filteredPlans.filter(plan => plan.status === selectedStatus);
        }
        
        setTripPlans(filteredPlans);
      } else {
        setError(result.error);
      }
    } catch (err) {
      console.error('Error loading trip plans:', err);
      setError('เกิดข้อผิดพลาดในการโหลดข้อมูลทริป');
    } finally {
      setLoading(false);
    }
  };

  // Load trip plans when component mounts or filters change
  useEffect(() => {
    loadTripPlans();
  }, [currentUser, searchTerm, selectedTheme, selectedStatus]);

  // Handle delete trip plan
  const handleDeleteTripPlan = async (tripPlan) => {
    if (!tripPlan || !tripPlan.id) {
      alert('ไม่พบข้อมูลทริปที่ต้องการลบ');
      return;
    }

    if (!canUserDeleteTripPlan(tripPlan, currentUser.uid, currentUser.role)) {
      alert('คุณไม่มีสิทธิ์ลบทริปนี้');
      return;
    }

    if (!window.confirm(`คุณแน่ใจหรือไม่ที่จะลบทริป "${tripPlan.tripName}"?`)) {
      return;
    }

    try {
      const result = await deleteTripPlan(tripPlan.id);
      if (result.success) {
        setTripPlans(prev => prev.filter(plan => plan.id !== tripPlan.id));
        alert('ลบทริปสำเร็จ');
      } else {
        alert(`เกิดข้อผิดพลาด: ${result.error}`);
      }
    } catch (err) {
      console.error('Error deleting trip plan:', err);
      alert('เกิดข้อผิดพลาดในการลบทริป');
    }
  };

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle theme filter
  const handleThemeFilter = (e) => {
    setSelectedTheme(e.target.value);
  };

  // Handle status filter
  const handleStatusFilter = (e) => {
    setSelectedStatus(e.target.value);
  };

  // Clear filters
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedTheme('');
    setSelectedStatus('');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="text-gray-600 mt-4">กำลังโหลดข้อมูลทริป...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center">
          <span className="text-red-500 text-2xl mr-3">❌</span>
          <div>
            <h3 className="text-red-800 font-semibold">เกิดข้อผิดพลาด</h3>
            <p className="text-red-600 text-sm mt-1">{error}</p>
          </div>
        </div>
        <button
          onClick={loadTripPlans}
          className="mt-4 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
        >
          ลองใหม่
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Trip Plans ของฉัน</h2>
          <p className="text-gray-600 mt-1">
            จัดการทริปของคุณเพื่อการจองคิวที่สะดวก
          </p>
        </div>
        <div className="text-sm text-gray-500">
          ทั้งหมด {tripPlans.length} ทริป
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ค้นหา
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearch}
              placeholder="ค้นหาชื่อทริป, สถานที่..."
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Theme Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Theme
            </label>
            <select
              value={selectedTheme}
              onChange={handleThemeFilter}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">ทุก Theme</option>
              {tripPlanModel.themeOptions.map(theme => (
                <option key={theme.value} value={theme.value}>
                  {theme.icon} {theme.label}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              สถานะ
            </label>
            <select
              value={selectedStatus}
              onChange={handleStatusFilter}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">ทุกสถานะ</option>
              {tripPlanModel.statusOptions.map(status => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>

          {/* Clear Filters */}
          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
            >
              🗑️ ล้างตัวกรอง
            </button>
          </div>
        </div>
      </div>

      {/* Trip Plans Grid */}
      {tripPlans.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <div className="text-6xl mb-4">🗺️</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {searchTerm || selectedTheme || selectedStatus ? 'ไม่พบทริปที่ตรงกับเงื่อนไข' : 'ยังไม่มีทริป'}
          </h3>
          <p className="text-gray-600 mb-6">
            {searchTerm || selectedTheme || selectedStatus 
              ? 'ลองเปลี่ยนเงื่อนไขการค้นหาหรือล้างตัวกรอง' 
              : 'สร้างทริปแรกของคุณเพื่อเริ่มต้นการวางแผน'
            }
          </p>
          {(searchTerm || selectedTheme || selectedStatus) && (
            <button
              onClick={clearFilters}
              className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
            >
              ล้างตัวกรอง
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tripPlans.map(tripPlan => (
            <TripPlanCard
              key={tripPlan.id}
              tripPlan={tripPlan}
              onEdit={onEditTripPlan}
              onDelete={handleDeleteTripPlan}
              onView={onViewTripPlan}
              onSelect={onSelectTripPlan}
              isSelectable={isSelectable}
              isSelected={selectedTripPlan?.id === tripPlan.id}
              showActions={!isSelectable}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default TripPlanList;
